import { computed } from 'vue'
import { ElMessage } from 'element-plus'
import config from '~/config'
import { confirmAustraliaComboOrder, deleteAustraliaMixedCartItems, getAustraliaMixedCartList, modifyAustraliaMixedCartItem } from '../api/combo'
import { australiaCartAmount, australiaCartKey, australiaCartMaximum, australiaCartQuantity, buildAustraliaCartConfirmation, isAustraliaCombo, normalizeAustraliaCartItem, resizeAustraliaBundle } from '../utils/cart'
import { australiaCartTotalUnits } from '../utils/cart'
import { australiaSpuDetailLink } from '../utils/purchase'

const storageKey = 'local_cart_items'
export function useAustraliaCart() {
  const items = useState('australia-cart-items', () => [])
  const loading = useState('australia-cart-loading', () => false)
  const submitting = useState('australia-cart-submitting', () => false)
  const updating = useState('australia-cart-updating', () => false)
  const error = useState('australia-cart-error', () => '')
  const drawerOpen = useState('australia-cart-drawer', () => false)
  const router = useRouter()
  const isLoggedIn = () => import.meta.client && !!localStorage.getItem(config.appTokenKey)
  const subtotal = computed(() => items.value.reduce((sum, item) => sum + australiaCartAmount(item), 0))
  const quantity = computed(() => items.value.reduce((sum, item) => sum + australiaCartTotalUnits(item), 0))
  const persist = () => { if (!isLoggedIn()) localStorage.setItem(storageKey, JSON.stringify(items.value)) }

  async function loadCart() {
    if (!import.meta.client || loading.value) return
    loading.value = true; error.value = ''
    try {
      let list
      if (isLoggedIn()) {
        const response = await getAustraliaMixedCartList()
        if (!response?.success) throw new Error(response?.msg || 'Unable to load your cart. Please try again.')
        list = response.data?.dataList || response.data || []
      } else {
        try { list = JSON.parse(localStorage.getItem(storageKey) || '[]') } catch { list = [] }
      }
      items.value = (Array.isArray(list) ? list : []).map(normalizeAustraliaCartItem)
    } catch (exception) { error.value = exception?.message || 'Unable to load your cart. Please try again.' }
    finally { loading.value = false }
  }
  async function openCart() { drawerOpen.value = true; await loadCart() }
  async function changeQuantity(item, delta) {
    if (updating.value || submitting.value) return
    const next = Math.max(1, Math.min(australiaCartMaximum(item), australiaCartQuantity(item) + delta))
    if (next === australiaCartQuantity(item) || australiaCartMaximum(item) === 0) return
    const selection = isAustraliaCombo(item) ? resizeAustraliaBundle(item, next) : null
    if (isAustraliaCombo(item) && !selection) { ElMessage.warning('Please reopen this bundle to change its flavour selection.'); return }
    updating.value = true
    try {
      if (isLoggedIn()) {
        const param = isAustraliaCombo(item)
          ? { itemType: 2, cartLineKey: item.cartLineKey, dealerShopId: item.dealerShopId, comboCode: item.comboCode, comboBuyQty: next, selectedSkuList: selection.map(({ dealerItemSkuId, buyAmount }) => ({ dealerItemSkuId, buyAmount })) }
          : { itemType: 1, dealerItemSkuId: item.dealerItemSkuId, buyAmount: next }
        const response = await modifyAustraliaMixedCartItem(param)
        if (!response?.success) throw new Error(response?.msg || 'Unable to update the quantity. Please try again.')
      }
      item.buyAmount = next
      if (isAustraliaCombo(item)) { item.comboBuyQty = next; item.selectedSkuList = selection; item.comboAmount = Number(item.comboUnitPrice ?? item.price ?? 0) * next }
      persist()
    } catch (exception) { ElMessage.error(exception?.message || 'Unable to update the quantity. Please try again.') }
    finally { updating.value = false }
  }
  async function removeItem(item) {
    if (updating.value || submitting.value) return
    updating.value = true
    try {
      if (isLoggedIn()) {
        const param = isAustraliaCombo(item) ? [{ itemType: 2, cartLineKey: item.cartLineKey }] : [{ itemType: 1, dealerItemSkuId: item.dealerItemSkuId }]
        const response = await deleteAustraliaMixedCartItems(param)
        if (!response?.success) throw new Error(response?.msg || 'Unable to remove this item.')
      }
      items.value = items.value.filter(candidate => australiaCartKey(candidate) !== australiaCartKey(item)); persist()
    } catch (exception) { ElMessage.error(exception?.message || 'Unable to remove this item.') }
    finally { updating.value = false }
  }
  async function goToCheckout() {
    if (submitting.value || updating.value || !items.value.length) return
    submitting.value = true
    try {
      const confirmOrderData = items.value.map(buildAustraliaCartConfirmation)
      const response = await confirmAustraliaComboOrder({ countryCode: 'AU', confirmOrderSkuReqDTOList: confirmOrderData })
      if (!response?.success) throw new Error(response?.msg || 'Unable to continue to checkout.')
      const data = response.data || {}
      localStorage.setItem('cart', JSON.stringify({ ...data, otherData: data, isComboOrder: true, confirmOrderData, confirmOrderSkuRespDTOList: data.confirmOrderSkuRespDTOList || [] }))
      drawerOpen.value = false; await router.push('/payment')
    } catch (exception) { ElMessage.error(exception?.message || 'Unable to continue to checkout.') }
    finally { submitting.value = false }
  }
  const getItemDetailLink = item => {
    const id = item.spItemSpuId || item.dealerItemSpuId || item.id || item.offerId
    return australiaSpuDetailLink({ ...item, id: isAustraliaCombo(item) ? undefined : id })
  }
  return { items, loading, submitting, updating, error, drawerOpen, subtotal, quantity, loadCart, openCart, changeQuantity, removeItem, goToCheckout, getItemDetailLink, isCombo: isAustraliaCombo, itemKey: australiaCartKey, itemQuantity: australiaCartQuantity, itemMaxQuantity: australiaCartMaximum, itemAmount: australiaCartAmount }
}
